import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import allocabLogo from '../assets/images/allocab-logo.svg';
import { Link } from '../components/atomic/Link';
import { TextInput } from '../components/atomic/TextInput';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { useResetPassword } from '../hooks/useResetPassword';
import { ToastManager } from '../services/toast/utils';
import { t } from '../services/translation/translation';

type FieldValues = {
  email: string;
};

export const ResetPasswordRoute = () => {
  const { state } = useLocation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FieldValues>({ defaultValues: { email: state?.initialEmail } });
  const navigate = useNavigate();
  const email = watch('email');

  const { mutate: resetPassword, isLoading } = useResetPassword({
    onSuccess: (data, variables) => {
      ToastManager.show({
        message: t('reset-password-route.success'),
        severity: 'success',
      });
      navigate('/login', { state: { initialEmail: variables.email } });
    },
  });

  const onSubmit = handleSubmit(({ email }) => {
    resetPassword({ email });
  });

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto min-h-screen">
        <div className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
          <img className="h-10 w-auto" src={allocabLogo} alt="allocab" />
        </div>
        <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <div className="space-y-2">
              <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
                {t('reset-password-route.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400"> {t('reset-password-route.description')}</p>
            </div>
            <form className="space-y-4 md:space-y-6" onSubmit={onSubmit}>
              <TextInput
                type="email"
                id="email"
                placeholder="jean@dupont.com"
                label={t('common.email')}
                errorMessage={errors.email?.message}
                {...register('email', { required: true })}
              />

              <PrimaryButton
                text={t('reset-password-route.cta')}
                type="submit"
                isLoading={isLoading}
                className="w-full"
              />
            </form>
            <p className="text-sm font-light text-gray-500 dark:text-gray-400">
              {t('sign-up-route.already-account')}
              <Link
                to="/login"
                state={{ initialEmail: email }}
                className="font-medium text-blue-600 hover:underline dark:text-blue-500 ml-1"
              >
                {t('sign-up-route.login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
